import React, { useState, useEffect } from 'react';
import Modal from '../ui/Modal';
import { eventsApi } from '../../api/eventsApi';
import { 
  DocumentChartBarIcon, 
  ChatBubbleLeftRightIcon, 
  ShareIcon, 
  QuestionMarkCircleIcon,
  ArrowPathIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';

const EventReportModal = ({ isOpen, onClose, event, onReportGenerated }) => {
  const [options, setOptions] = useState({
    chatbot: true,
    graph: true,
    qa: false,
    push_to_wordpress: false
  });
  const [questions, setQuestions] = useState('');
  const [chatbotLink, setChatbotLink] = useState('');
  const [chatbotName, setChatbotName] = useState('');
  const [chatbotDisclaimer, setChatbotDisclaimer] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setError('');
      setOptions({ chatbot: true, graph: true, qa: false, push_to_wordpress: false });
      setQuestions('');
      setChatbotLink('');
      setChatbotName('');
      setChatbotDisclaimer('');
    }
  }, [isOpen]);

  const handleOptionChange = (option) => {
    setOptions(prev => ({
      ...prev,
      [option]: !prev[option]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setLoading(true);
    setError('');

    try {
      // Parse questions (one per line)
      const questionsList = questions
        .split('\n')
        .map(q => q.trim())
        .filter(q => q.length > 0);

      await eventsApi.generateReport(event.id, {
        questions: questionsList,
        chatbot: options.chatbot,
        qa: options.qa && questionsList.length > 0,
        graph: options.graph,
        push_to_wordpress: options.push_to_wordpress,
        chatbot_link: options.chatbot ? chatbotLink : undefined,
        chatbot_name: options.chatbot ? chatbotName : undefined,
        chatbot_disclaimer: options.chatbot ? chatbotDisclaimer : undefined
      });

      // Close modal and notify parent that generation started
      if (onReportGenerated) {
        onReportGenerated('started');
      }
      onClose();
      
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to start report generation');
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Generate Event Report" size="lg">
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Info */}
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-sm text-gray-600">
            Generate a comprehensive report for <span className="font-medium text-gray-900">{event?.title}</span> including 
            statistics, analysis, and optional chatbot/graph features.
          </p>
        </div>

        {/* Generation Options */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Report Options
          </label>
          <div className="space-y-3">
            {/* Chatbot */}
            <div className="p-3 border border-gray-200 rounded-lg">
              <label className="flex items-start gap-3 hover:bg-gray-50 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={options.chatbot}
                  onChange={() => handleOptionChange('chatbot')}
                  className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  disabled={loading}
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <ChatBubbleLeftRightIcon className="h-5 w-5 text-blue-600" />
                    <span className="font-medium text-gray-900">Create Chatbot</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Create an AI chatbot that can answer questions about this event's transcripts
                  </p>
                </div>
              </label>
              
              {/* Chatbot input fields - shown when chatbot is enabled */}
              {options.chatbot && (
                <div className="mt-4 ml-7 space-y-3 pt-3 border-t border-gray-200">
                  <div>
                    <label htmlFor="chatbot-link" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Chatbot Link
                    </label>
                    <input
                      id="chatbot-link"
                      type="url"
                      value={chatbotLink}
                      onChange={(e) => setChatbotLink(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="https://example.com/chatbot"
                      disabled={loading}
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="chatbot-name" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Chatbot Name
                    </label>
                    <input
                      id="chatbot-name"
                      type="text"
                      value={chatbotName}
                      onChange={(e) => setChatbotName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="Event Chatbot"
                      disabled={loading}
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="chatbot-disclaimer" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Disclaimer
                    </label>
                    <textarea
                      id="chatbot-disclaimer"
                      value={chatbotDisclaimer}
                      onChange={(e) => setChatbotDisclaimer(e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="Enter disclaimer text for the chatbot..."
                      disabled={loading}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Knowledge Graph */}
            <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
              <input
                type="checkbox"
                checked={options.graph}
                onChange={() => handleOptionChange('graph')}
                className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                disabled={loading}
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <ShareIcon className="h-5 w-5 text-purple-600" />
                  <span className="font-medium text-gray-900">Generate Knowledge Graph</span>
                </div>
                <p className="text-sm text-gray-500 mt-0.5">
                  Create an interactive graph showing speakers, arguments, and topics
                </p>
              </div>
            </label>

            {/* Q&A Analysis */}
            <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
              <input
                type="checkbox"
                checked={options.qa}
                onChange={() => handleOptionChange('qa')}
                className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                disabled={loading}
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <QuestionMarkCircleIcon className="h-5 w-5 text-orange-600" />
                  <span className="font-medium text-gray-900">Q&A Analysis</span>
                </div>
                <p className="text-sm text-gray-500 mt-0.5">
                  Analyze specific questions across all session transcripts
                </p>
              </div>
            </label>

            {/* Publish to WordPress */}
            <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
              <input
                type="checkbox"
                checked={options.push_to_wordpress}
                onChange={() => handleOptionChange('push_to_wordpress')}
                className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                disabled={loading}
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <GlobeAltIcon className="h-5 w-5 text-green-600" />
                  <span className="font-medium text-gray-900">Publish to WordPress</span>
                </div>
                <p className="text-sm text-gray-500 mt-0.5">
                  Automatically publish the event report to WordPress (DigWatch) after generation
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* Questions (only if Q&A is enabled) */}
        {options.qa && (
          <div>
            <label htmlFor="questions" className="block text-sm font-medium text-gray-700 mb-2">
              Questions for Analysis
            </label>
            <textarea
              id="questions"
              value={questions}
              onChange={(e) => setQuestions(e.target.value)}
              rows={5}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              placeholder="Enter one question per line, e.g.:&#10;What were the main conclusions?&#10;What topics received the most attention?&#10;Were there any disagreements?"
              disabled={loading}
            />
            <p className="mt-1.5 text-xs text-gray-500">
              Enter one question per line. The AI will search all session transcripts to find relevant discussions.
            </p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            disabled={loading}
          >
            {loading ? (
              <>
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
                Starting...
              </>
            ) : (
              <>
                <DocumentChartBarIcon className="h-4 w-4" />
                Generate Report
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default EventReportModal;
